import { useEffect, useState } from "react";
import { usePageSEO } from "../hooks/usePageSEO";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LOGO_URL } from "../lib/brand";
import { useAuth, formatApiError } from "../lib/auth";
import { AlertCircle, CheckCircle2, Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  usePageSEO({ title: "Passwort zurücksetzen · Alphalane", noIndex: true });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Mindestens 8 Zeichen."); return; }
    if (pw !== confirm) { setError("Passwörter stimmen nicht überein."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, password: pw }, { withCredentials: true });
      setDone(true);
      toast.success("Passwort geändert!");
      if (data.auto_login) {
        await refresh();
        setTimeout(() => navigate("/onboarding"), 800);
      } else {
        setTimeout(() => navigate("/login"), 1000);
      }
    } catch (err) {
      // Prefer the FastAPI ``detail`` field; fall back to HTTP status + Axios
      // message so the user (and we, debugging Production) see something
      // actionable instead of the opaque "Etwas ist schiefgelaufen.".
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (detail != null) {
        setError(formatApiError(detail));
      } else if (status) {
        setError(`Reset fehlgeschlagen (HTTP ${status}). Bitte fordere einen neuen Link an oder kontaktiere uns.`);
      } else {
        setError(
          err?.message
            ? `Netzwerkfehler: ${err.message}. Prüfe deine Internetverbindung oder fordere einen neuen Reset-Link an.`
            : "Reset fehlgeschlagen — bitte fordere einen neuen Link an.",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] flex flex-col" data-testid="reset-page">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display font-semibold text-[15px]"><span className="al-grad-text">ALPHALANE</span></div>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 sm:px-8 py-10">
        <div className="w-full max-w-md">
          <div className="al-card p-7 sm:p-9">
            {done ? (
              <div className="text-center" data-testid="reset-done">
                <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500 text-white flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <h1 className="font-display font-bold text-2xl">Passwort geändert!</h1>
                <p className="mt-3 text-sm text-slate-600">Du wirst gleich weitergeleitet…</p>
              </div>
            ) : !token ? (
              <div className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mb-4">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <h1 className="font-display font-bold text-2xl">Link unvollständig</h1>
                <p className="mt-3 text-sm text-slate-600">Der Reset-Link ist nicht korrekt. Bitte fordere einen neuen über den Login an.</p>
                <Link to="/login" className="al-cta-ghost mt-6 inline-flex">Zurück zum Login</Link>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-xl al-grad-bg text-white flex items-center justify-center">
                    <KeyRound className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="al-tag">Neues Passwort</span>
                    <h1 className="mt-1 font-display font-bold text-2xl sm:text-3xl">Setze dein Passwort neu.</h1>
                  </div>
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                  <PasswordField label="Neues Passwort" value={pw} onChange={(e) => setPw(e.target.value)} show={show} onToggle={() => setShow(!show)} testid="reset-password" />
                  <PasswordField label="Passwort bestätigen" value={confirm} onChange={(e) => setConfirm(e.target.value)} show={show} onToggle={() => setShow(!show)} testid="reset-password-confirm" />
                  {error && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2" data-testid="reset-error">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                  <button type="submit" disabled={busy} className="al-cta w-full justify-center" data-testid="reset-submit">
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Speichere…</> : <>Passwort speichern <KeyRound className="h-4 w-4" /></>}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

const PasswordField = ({ label, value, onChange, show, onToggle, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</span>
    <div className="mt-1.5 relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        required
        minLength={8}
        data-testid={testid}
        className="w-full rounded-xl border border-[var(--al-line)] bg-white px-4 py-2.5 pr-11 text-sm focus:outline-none focus:border-[var(--al-purple)] focus:ring-2 focus:ring-[var(--al-purple)]/20"
      />
      <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  </label>
);
